// Picks the interface language before anything is rendered.
//
// Order of preference: the language chosen in the settings (stored in
// localStorage), then the browser languages, then the `lang` attribute of the
// page, then English. The result is written to `<html lang>`, which is what
// `loadLocale()` and the Intl polyfills read.

// Locales that ship a vanilla Mastodon file *and* a glitch file, i.e. the ones
// `loadLocale()` can import. Keep in sync with the *.json files in
// app/javascript/mastodon/locales and app/javascript/flavours/glitch/locales.
export const AVAILABLE_LOCALES: readonly string[] = [
  "af",
  "an",
  "ar",
  "ast",
  "be",
  "bg",
  "bn",
  "br",
  "bs",
  "ca",
  "ckb",
  "co",
  "cs",
  "cy",
  "da",
  "de",
  "el",
  "en",
  "en-GB",
  "eo",
  "es",
  "es-AR",
  "es-MX",
  "et",
  "eu",
  "fa",
  "fi",
  "fo",
  "fr",
  "fr-QC",
  "fy",
  "ga",
  "gd",
  "gl",
  "he",
  "hi",
  "hr",
  "hu",
  "hy",
  "id",
  "ig",
  "io",
  "is",
  "it",
  "ja",
  "ka",
  "kab",
  "kk",
  "kn",
  "ko",
  "ku",
  "kw",
  "la",
  "lt",
  "lv",
  "mk",
  "ml",
  "mr",
  "ms",
  "my",
  "nl",
  "nn",
  "no",
  "oc",
  "pa",
  "pl",
  "pt-BR",
  "pt-PT",
  "ro",
  "ru",
  "sa",
  "sc",
  "sco",
  "si",
  "sk",
  "sl",
  "sq",
  "sr",
  "sr-Latn",
  "sv",
  "szl",
  "ta",
  "tai",
  "te",
  "th",
  "tr",
  "tt",
  "ug",
  "uk",
  "ur",
  "vi",
  "zgh",
  "zh-CN",
  "zh-HK",
  "zh-TW",
];

export const AUTO_LOCALE = "auto";

const STORAGE_KEY = "masto-fe-locale";

const AVAILABLE_BY_LOWER_CASE = new Map(AVAILABLE_LOCALES.map((locale) => [locale.toLowerCase(), locale]));

// Browser language tags that differ from the names used for the locale files.
const ALIASES = new Map([
  ["nb", "no"],
  ["fr-ca", "fr-QC"],
  ["zh-hans", "zh-CN"],
  ["zh-sg", "zh-CN"],
  ["zh-hant", "zh-TW"],
  ["zh-mo", "zh-HK"],
]);

function matchExactly(tag: string): string | undefined {
  const lower = tag.toLowerCase();
  const alias = ALIASES.get(lower)?.toLowerCase();

  return AVAILABLE_BY_LOWER_CASE.get(lower) ?? (alias ? AVAILABLE_BY_LOWER_CASE.get(alias) : undefined);
}

// Matches a browser language tag like "de-AT" or "zh-Hant-TW" with the best
// available locale by dropping subtags from the right ("de-AT" -> "de").
function matchTag(tag: string): string | undefined {
  const parts = tag.split("-");

  for (let length = parts.length; length > 0; length--) {
    const match = matchExactly(parts.slice(0, length).join("-"));

    if (match) {
      return match;
    }
  }

  // A bare language such as "pt" falls back to the first regional variant.
  const [language = tag] = parts;

  return AVAILABLE_LOCALES.find((locale) => locale.toLowerCase().startsWith(`${language.toLowerCase()}-`));
}

export function resolveLocale(languages: readonly string[]): string | undefined {
  for (const tag of languages) {
    const match = matchTag(tag);

    if (match) {
      return match;
    }
  }

  return undefined;
}

// Returns the stored locale code, or AUTO_LOCALE if none is stored.
export function getStoredLocale(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    if (stored && AVAILABLE_LOCALES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage can be unavailable (private mode, blocked site data)
  }

  return AUTO_LOCALE;
}

export function setStoredLocale(locale: string): void {
  try {
    if (locale === AUTO_LOCALE) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, locale);
    }
  } catch {
    // see getStoredLocale()
  }
}

export function applyPreferredLocale(): string {
  const stored = getStoredLocale();

  let locale: string | undefined;

  if (stored === AUTO_LOCALE) {
    const languages = navigator.languages.length > 0 ? navigator.languages : [navigator.language];

    locale = resolveLocale(languages) ?? resolveLocale([document.documentElement.lang]);
  } else {
    locale = stored;
  }

  locale ??= "en";
  document.documentElement.lang = locale;

  return locale;
}
